import { createRouter, createWebHistory } from 'vue-router';
import axios from 'axios';
import LoginView from '../components/Login.vue';
import QrScanner from '../components/QrScanner.vue';
import RegisterView from '../components/RegisterView.vue'; // Import the Register component
import AdminDashboard from '../components/AdminDashboard.vue'; // Import the Admin Dashboard component
import ReviewCheckedItems from '../components/ReviewCheckedItems.vue'; // Import the Review Checked Items component
import CalendarView from '../components/CalendarView.vue'; // Adjust path if necessary
import ProfilePage from '../components/ProfilePage.vue';

const routes = [
  {
    path: '/',
    name: 'Login',
    component: LoginView,
  },
  {
    path: '/register',
    name: 'Register',
    component: RegisterView,
  },
  {
    path: '/scanner',
    name: 'QrScanner',
    component: QrScanner,
    meta: {
      requiresAuth: true,
      allowedRoles: ['trainer', 'admin'], // Only trainers should access the scanner
    },
  },
  {
    path: '/admin',
    name: 'AdminDashboard',
    component: AdminDashboard,
    meta: {
      requiresAuth: true,
      allowedRoles: ['admin'], // Only admins should access the Admin Dashboard
    },
  },
  {
    path: '/calendar',
    name: 'CalendarView',
    component: CalendarView,
    meta: {
      requiresAuth: true, // Add any required meta like authentication
      allowedRoles: ['admin'],
    },
  },
  {
    path: '/review-checked-items',
    name: 'ReviewCheckedItems',
    component: ReviewCheckedItems,
    meta: {
      requiresAuth: true,
      allowedRoles: ['trainer', 'admin'], // Trainers and admins should access this page
    },
  },
  {
    path: '/profile',
    name: 'ProfilePage',
    component: ProfilePage,
    meta: {
      requiresAuth: true,
      allowedRoles: ['trainer', 'admin'], // Trainers and admins should access this page
    },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

// Navigation Guard to protect routes
router.beforeEach(async (to, from, next) => {
  // console.log('Checking authentication for route:', to.path); // Log the route being accessed
  const token = localStorage.getItem('authToken');
  // console.log('Auth token:', token); // Log the token found in localStorage

  if (to.matched.some((record) => record.meta.requiresAuth)) {
    if (!token) {
      // If token is not found, redirect to login
      next({ path: '/' });
    } else {
      try {
        // Verify the token by making a request to the backend
        const response = await axios.post('https://api.inventory.stafftraining.me/api/verifyToken', {}, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const userRole = response.data.role; // Get the user role from the backend response

        if (to.matched.some((record) => record.meta.allowedRoles)) {
          // If route has restricted access based on roles
          if (to.meta.allowedRoles.includes(userRole)) {
            next(); // Role is allowed, proceed to route
          } else {
            console.error('Access denied: User does not have the required role');
            next({ path: '/' }); // Redirect to login if role is not allowed
          }
        } else {
          next(); // If no specific role restriction, proceed to the route
        }
      } catch (error) {
        console.error('Token validation failed:', error);
        // If token validation fails, clear the token and redirect to login
        localStorage.removeItem('authToken');
        next({ path: '/' });
      }
    }
  } else if (to.path === '/' && token) {
    // If trying to access the login page while logged in, redirect based on user role
    try {
      const response = await axios.post('https://api.inventory.stafftraining.me/api/verifyToken', {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const userRole = response.data.role;

      if (userRole === 'admin') {
        next({ path: '/admin' }); // Redirect admin to admin dashboard
      } else if (userRole === 'trainer') {
        next({ path: '/scanner' }); // Redirect trainer to scanner
      } else {
        next(); // Default action if role is not explicitly handled
      }
    } catch (error) {
      console.error('Token validation failed:', error);
      localStorage.removeItem('authToken');
      next(); // Let user see the login page if token validation fails
    }
  } else {
    next(); // Proceed to the requested route if no authentication is required
  }
});

export default router;
